import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { post } from '@/lib/api'

const PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const

export function NewTaskModal({
  open, onClose, onCreated, defaultEntity,
}: {
  open: boolean
  onClose: () => void
  onCreated?: () => void
  defaultEntity?: { type: 'case' | 'lead' | 'contact' | 'opportunity' | 'account'; id: string }
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<typeof PRIORITIES[number]>('medium')
  const [dueDate, setDueDate] = useState('')
  const [reminder, setReminder] = useState(false)

  const create = useMutation({
    mutationFn: () => post('/api/v1/tasks', {
      title,
      description: description || undefined,
      priority,
      dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
      reminderAt: reminder && dueDate ? new Date(dueDate).toISOString() : undefined,
      entityType: defaultEntity?.type,
      entityId: defaultEntity?.id,
    }),
    onSuccess: () => {
      toast.success('Task created')
      onCreated?.()
      reset()
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function reset() {
    setTitle(''); setDescription(''); setPriority('medium'); setDueDate(''); setReminder(false)
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-20"
      style={{ background: 'rgba(15,23,42,0.45)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl"
        style={{ width: 480, maxWidth: '95vw' }}
      >
        <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#0f172a] m-0">New task</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f8fafc] text-[#94a3b8] hover:text-[#0f172a]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <Field label="Title">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Follow up with Acme"
              className="w-full bg-white border border-[#e2e8f0] rounded-lg px-3 py-2 text-sm text-[#0f172a] focus:border-[#2B8FD4] outline-none"
            />
          </Field>
          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full bg-white border border-[#e2e8f0] rounded-lg px-3 py-2 text-sm text-[#0f172a] focus:border-[#2B8FD4] outline-none resize-none"
            />
          </Field>
          <Field label="Priority">
            <div className="flex gap-2">
              {PRIORITIES.map((p) => (
                <button
                  key={p}
                  onClick={() => setPriority(p)}
                  className="flex-1 rounded-lg border px-3 py-2 text-xs font-semibold capitalize"
                  style={{
                    borderColor: priority === p ? '#2B8FD4' : '#e2e8f0',
                    background: priority === p ? '#EFF6FF' : 'white',
                    color: priority === p ? '#2B8FD4' : '#64748b',
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Due">
            <input
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full bg-white border border-[#e2e8f0] rounded-lg px-3 py-2 text-sm text-[#0f172a] focus:border-[#2B8FD4] outline-none"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-[#0f172a]">
            <input type="checkbox" checked={reminder} onChange={(e) => setReminder(e.target.checked)} />
            Send a reminder at the due time
          </label>
        </div>
        <div className="px-6 py-4 border-t border-[#e2e8f0] flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm text-[#64748b] px-4 py-2 hover:text-[#0f172a]">Cancel</button>
          <button
            onClick={() => create.mutate()}
            disabled={!title.trim() || create.isPending}
            className="bg-[#2B8FD4] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#1a7bc4] transition-colors disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'Create task'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  )
}
